'use client';

import React from 'react';
import useSWR from 'swr';
import { motion } from 'framer-motion';
import { getShopProducts } from '@/clients/customer/shop-products';
import ShopProductCard from './ShopProductCard';
import { TrendingItemsType } from '@/lib/types/customer/TrendingItemsType';

type ShopProductsProps = {
  lang: string;
  params?: Record<string, string>;
  dataStore: any;
};

const fetcher = async (locale?: string) => {
  return getShopProducts(
    {
      per_page: 6,
      order_dir: 'asc',
      order_by: 'order_index',
    },
    {
      ['Accept-Language']: locale || 'en',
    }
  ).then((res) => res?.data);
};

export default function ShopProducts({
  lang = 'de',
  params,
  dataStore,
}: ShopProductsProps) {
  const sectionData: any = dataStore?.value;
  const {
    data: resData,
    error,
    isLoading,
  } = useSWR(
    ['shop-products', params?.locale],
    ([, locale]: string[]) => fetcher(locale),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (isLoading) {
    return (
      <section className="relative w-full bg-white py-12 lg:py-[90px]">
        <div className="container">
          <div className="heading mb-10 flex w-full flex-col justify-center lg:flex-row lg:items-start lg:justify-start lg:space-x-20">
            <h2 className="text-h3 font-black leading-[110%] text-title sm:text-h2 md:text-h1">
              {sectionData?.title?.[lang] ?? 'VOLL IM TREND!'}
            </h2>
            <p className="mt-2 text-h6 font-bold leading-[150%] text-paragraph md:text-h4 lg:max-w-[240px]">
              {sectionData?.sub_title?.[lang] ??
                'Die Lieblingsprodukte unserer Kunden'}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return null; // Silently fail - don't show section if there's an error
  }

  // Don't render section if no products
  if (!resData?.data || resData.data.length === 0) {
    return null;
  }

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  return (
    <section className="relative w-full bg-white py-16 lg:py-28">
      <div className="container">
        <div className="heading mb-10 flex w-full flex-col justify-center lg:flex-row lg:items-center lg:justify-start lg:space-x-20">
          <h2 className="text-h3 font-black leading-[110%] text-title sm:text-h2 md:text-h1">
            {sectionData?.title?.[lang] ?? 'VOLL IM TREND!'}
          </h2>
          <p className="mt-2 text-h6 font-bold leading-[150%] text-paragraph md:text-h4 lg:max-w-[240px]">
            {sectionData?.sub_title?.[lang] ??
              'Die Lieblingsprodukte unserer Kunden'}
          </p>
        </div>

        <motion.div
          className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {resData.data.slice(0, 6).map((product: TrendingItemsType) => {
            return (
              <React.Fragment key={product.id}>
                <ShopProductCard trendingItem={product} />
              </React.Fragment>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

const ProductCardSkeleton = () => (
  <div className="h-[320px] w-full animate-pulse overflow-hidden rounded-lg bg-slate-200">
    <div className="h-[220px] rounded-t-md bg-slate-300"></div>
    <div className="space-y-3 p-4">
      <div className="h-5 w-3/4 rounded bg-slate-300"></div>
      <div className="h-3 w-full rounded bg-slate-300"></div>
      <div className="h-3 w-5/6 rounded bg-slate-300"></div>
    </div>
  </div>
);
